import { useState, useEffect, useMemo } from 'react';
import { Clock } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fetchWithAuth } from '../../../../js/authToken';
import API_BASE_URL from '../../../../js/urlHelper';
import FetchWithGif from '../../../../components/Reutilizables/FetchWithGif';
import ReporteCronograma from './ReporteCronograma';
import ExtornarPrestamoModal from './modals/ExtornarPrestamoModal';
import CapturaAbonoModal from './modals/CapturaAbonoModal';
import { toast } from 'react-toastify';

const Cronograma = ({ allowExtornar = true, apiBasePath = '/api/admin', allowGroupSearch = true }) => {
  const [isGroupSearch, setIsGroupSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [groups, setGroups] = useState([]);
  const [prestamos, setPrestamos] = useState([]);
  const [selectedPrestamo, setSelectedPrestamo] = useState(null);
  const [cuotas, setCuotas] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showReporte, setShowReporte] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showExtornarModal, setShowExtornarModal] = useState(false);
  const [showCapturaAbonoModal, setShowCapturaAbonoModal] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfFileName, setPdfFileName] = useState('');
  const [horaCobranzaDraft, setHoraCobranzaDraft] = useState('');
  const [savingHoraCobranza, setSavingHoraCobranza] = useState(false);
  const [showHoraCobranzaEditor, setShowHoraCobranzaEditor] = useState(false);

  const prestamoSeleccionado = useMemo(() => {
    return prestamos.find((p) => p.idPrestamo === selectedPrestamo) || {};
  }, [prestamos, selectedPrestamo]);

  const horaCobranza = prestamoSeleccionado?.hora_cobranza || null;

  useEffect(() => {
    setHoraCobranzaDraft(horaCobranza || '');
    setShowHoraCobranzaEditor(false);
  }, [horaCobranza, selectedPrestamo]);

  const clienteReporte = useMemo(() => {
    const direcciones = prestamoSeleccionado.direcciones || [];
    const contactos = prestamoSeleccionado.contactos || [];
    const cuentas = prestamoSeleccionado.cuentasBancarias || [];

    const direccionFiscalObj = direcciones.find(
      (dir) => dir.tipo && dir.tipo.toLowerCase() === 'fiscal'
    );

    const direccionFiscal = direccionFiscalObj
      ? [
        direccionFiscalObj.tipoVia,
        direccionFiscalObj.nombreVia,
        direccionFiscalObj.numeroMz,
        direccionFiscalObj.urbanizacion,
        direccionFiscalObj.distrito,
        direccionFiscalObj.provincia,
        direccionFiscalObj.departamento,
      ]
        .filter(Boolean)
        .join(' ')
      : 'Sin dirección fiscal';

    const telefonoObj = contactos.find(
      (contacto) => contacto.tipo && contacto.tipo.toLowerCase() === 'principal'
    );

    const telefono =
      telefonoObj && telefonoObj.telefono ? telefonoObj.telefono : 'Sin teléfono';

    const numeroCuenta =
      cuentas[0] && cuentas[0].numeroCuenta ? cuentas[0].numeroCuenta : 'Sin cuenta';

    return {
      nombreCompleto: prestamoSeleccionado.cliente || 'N/A',
      dni: prestamoSeleccionado.dni || 'N/A',
      direccion: direccionFiscal,
      numeroCuenta: numeroCuenta,
      telefono: telefono,
      idCliente: prestamoSeleccionado.idCliente || 'N/A',
      producto: prestamoSeleccionado.producto || 'Sin producto',
      analista: prestamoSeleccionado.asesor || 'Sin asesor',
    };
  }, [prestamoSeleccionado]);

  const esReprogramado = useMemo(() => {
    return (
      prestamoSeleccionado?.esReprogramado === true ||
      (prestamoSeleccionado?.estado_actual || '').toLowerCase() === 'reprogramado'
    );
  }, [prestamoSeleccionado]);

  const esConvenio = useMemo(() => {
    return (
      prestamoSeleccionado?.esConvenio === true ||
      (prestamoSeleccionado?.estado_actual || '').toLowerCase() === 'refinanciado'
    );
  }, [prestamoSeleccionado]);

  const cuotasDeConvenio = useMemo(() => {
    if (!esConvenio || !cuotas || cuotas.length === 0) return [];
    const filtradas = cuotas.filter(
      (c) =>
        typeof c?.observaciones === 'string' &&
        c.observaciones.toLowerCase().includes('convenio') &&
        !c.observaciones.toLowerCase().includes('cerrada por convenio')
    );
    return filtradas.length > 0 ? filtradas : [];
  }, [cuotas, esConvenio]);

  const cuotasVigentes = useMemo(() => {
    if (!cuotas || cuotas.length === 0) return [];
    return cuotas.filter(c => c.estado !== 'pagado' && c.estado !== 'Pagado');
  }, [cuotas]);

    const cuotasAMostrar = useMemo(() => {
    if (!cuotas || cuotas.length === 0) return [];

    if (esReprogramado) {
      return cuotasVigentes;
    }

    if (esConvenio) {
      if (cuotasDeConvenio.length > 0) return cuotasDeConvenio;
      return cuotasVigentes;
    }

    return cuotas;
  }, [cuotas, esReprogramado, esConvenio, cuotasVigentes, cuotasDeConvenio]);

  const mostrarColumnaEstado = useMemo(() => {
    if (!cuotasAMostrar || cuotasAMostrar.length === 0) return false;
    return cuotasAMostrar.some(c => c.estado === 'pagado' || c.estado === 'Pagado');
  }, [cuotasAMostrar]);

  const totalesCalculados = useMemo(() => {
    if (!cuotasAMostrar || cuotasAMostrar.length === 0) {
      return { capital: 0, interes: 0, otros: 0, cuota: 0 };
    }

    const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

    const totales = cuotasAMostrar.reduce(
      (acc, c) => {
        const capital = parseFloat(c.capital || 0);
        const interes = parseFloat(c.interes || 0);
        const otros =
          c.otros !== null && c.otros !== undefined
            ? parseFloat(c.otros)
            : parseFloat(c.monto || 0) - capital - interes;
        const cuota = parseFloat(c.monto || 0);

        return {
          capital: acc.capital + capital,
          interes: acc.interes + interes,
          otros: acc.otros + otros,
          cuota: acc.cuota + cuota,
        };
      },
      { capital: 0, interes: 0, otros: 0, cuota: 0 }
    );

    const capitalObjetivo = parseFloat(prestamoSeleccionado?.monto ?? 0);
    if (capitalObjetivo > 0 && Math.abs(capitalObjetivo - totales.capital) <= 0.05) {
      totales.capital = capitalObjetivo;
      totales.otros = round2(totales.cuota - totales.capital - totales.interes);
    }

    return {
      capital: round2(totales.capital),
      interes: round2(totales.interes),
      otros: round2(totales.otros),
      cuota: round2(totales.cuota),
    };
  }, [cuotasAMostrar, prestamoSeleccionado]);

  const handleDownloadPDF = () => {
    if (pdfBlob && pdfFileName) {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(pdfBlob);
      link.download = pdfFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }
  };

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_BASE_URL}${apiBasePath}/cronograma/grupos`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          }
        );
        const data = await response.json();
        setGroups(data);
      } catch (err) {
        setError('Error al cargar grupos');
      }
    };
    fetchGroups();
  }, [apiBasePath]);

  useEffect(() => {
    if (!allowGroupSearch) {
      setIsGroupSearch(false);
      setSelectedGroup('');
    }
  }, [allowGroupSearch]);

  const handleSearch = async () => {
    const groupSearchEnabled = allowGroupSearch && isGroupSearch;
    setError('');
    setPrestamos([]);
    setSelectedPrestamo(null);
    setCuotas([]);
    setPdfUrl(null);
    setLoading(true);

    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}${apiBasePath}/cronograma/buscar`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            searchTerm: groupSearchEnabled ? selectedGroup : searchTerm,
            isGroupSearch: groupSearchEnabled,
          }),
        }
      );
      const responseData = await response.json();
      if (responseData.message && !responseData.prestamos?.length) {
        setError(responseData.message);
      }
      const prestamosData = Array.isArray(responseData.prestamos)
        ? responseData.prestamos
        : [];
      setPrestamos(prestamosData);
    } catch (err) {
      setError(err.message || 'Error al buscar préstamos');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCronograma = async (prestamoId) => {
    setLoading(true);
    setError('');
    setCuotas([]);
    setPdfUrl(null);
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}${apiBasePath}/cronograma/generar/${prestamoId}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      if (!response.ok) {
        throw new Error('Error al generar el cronograma');
      }
      const data = await response.json();
      setCuotas(data.cuotas || []);
      setPdfUrl(data.pdf_url || null);
      if (data.prestamo?.idPrestamo) {
        setPrestamos((prevPrestamos) =>
          prevPrestamos.map((prestamo) =>
            prestamo.idPrestamo === data.prestamo.idPrestamo
              ? { ...prestamo, ...data.prestamo }
              : prestamo
          )
        );
      }
      setSelectedPrestamo(prestamoId);
    } catch (err) {
      setError(err.message || 'Error al generar el cronograma');
    } finally {
      setLoading(false);
    }
  };

  const handleExtornarPrestamo = () => {
    if (!allowExtornar) {
      return;
    }
    setShowExtornarModal(true);
  };

  const handleSaveHoraCobranza = async () => {
    if (!selectedPrestamo) {
      toast.error('Primero seleccione un préstamo');
      return;
    }

    if (horaCobranzaDraft && !/^([01]\d|2[0-3]):[0-5]\d$/.test(horaCobranzaDraft)) {
      toast.error('La hora de cobranza debe tener el formato HH:mm');
      return;
    }

    setSavingHoraCobranza(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}${apiBasePath}/prestamos/${selectedPrestamo}/hora-cobranza`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ hora_cobranza: horaCobranzaDraft || null }),
        }
      );
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Error al actualizar hora de cobranza');
      }

      const nextHoraCobranza = data.data?.hora_cobranza || null;
      setPrestamos((prevPrestamos) =>
        prevPrestamos.map((prestamo) =>
          prestamo.idPrestamo === selectedPrestamo
            ? { ...prestamo, hora_cobranza: nextHoraCobranza }
            : prestamo
        )
      );
      setCuotas((prevCuotas) =>
        prevCuotas.map((cuota) => ({
          ...cuota,
          hora_cobranza: nextHoraCobranza,
        }))
      );
      toast.success('Hora de cobranza actualizada correctamente');
      setShowHoraCobranzaEditor(false);
    } catch (error) {
      toast.error(error.message || 'Error al actualizar hora de cobranza');
    } finally {
      setSavingHoraCobranza(false);
    }
  };

  const registrarExtornoPrestamo = async () => {
    if (!allowExtornar) {
      return;
    }
    setLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}${apiBasePath}/prestamos/extornar/${selectedPrestamo}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Error al extornar el préstamo');
      }

      toast.success('Préstamo extornado correctamente');
      setShowExtornarModal(false);
      setPrestamos([]);
      setSelectedPrestamo(null);
      setCuotas([]);
      setPdfUrl(null);
      setSearchTerm('');
      setSelectedGroup('');
      setError('');
    } catch (error) {
      toast.error(error.message || 'Error al extornar el préstamo');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy');
    } catch (error) {
      return '-';
    }
  };

  const isPrestamoCreadoHoy = useMemo(() => {
    if (!prestamoSeleccionado?.fecha_generacion) {
      return false;
    }

    try {
      const hoyLima = new Date().toLocaleDateString('en-CA', {
        timeZone: 'America/Lima'
      });

      let fechaPrestamo = prestamoSeleccionado.fecha_generacion;
      if (fechaPrestamo.includes('/')) {
        const [day, month, year] = fechaPrestamo.split('/');
        fechaPrestamo = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      } else if (fechaPrestamo.includes('T')) {
        fechaPrestamo = fechaPrestamo.split('T')[0];
      }

      return fechaPrestamo === hoyLima;
    } catch (error) {
      console.error('Error al validar fecha de préstamo:', error);
      return false;
    }
  }, [prestamoSeleccionado]);

  const labelClass = 'mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-600';
  const inputClass = 'w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200';
  const selectClass = inputClass;
  const primaryButtonClass = 'w-full rounded-xl bg-red-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-400 md:w-auto';
  const actionButtonClass = 'inline-flex items-center justify-center rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition';

  return (
    <div className="space-y-6">
      {loading && <FetchWithGif />}
      <section className="ui-card relative overflow-hidden px-6 py-7 md:px-8">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_88%_10%,rgba(217,44,52,0.12),transparent_38%)]" />
        <div className="relative">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-red-600">Gestión de cartera</p>
          <h2 className="mt-2 text-2xl font-semibold text-slate-900 md:text-3xl">Cronograma de pagos</h2>
          <p className="mt-2 text-sm text-slate-600">
            Busca préstamos por cliente o grupo y revisa su cronograma detallado en una sola vista.
          </p>
        </div>
      </section>

      <section className="ui-card p-6">
        <div className="max-w-7xl">
          {allowGroupSearch && (
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="groupSearch"
                checked={isGroupSearch}
                onChange={() => setIsGroupSearch(!isGroupSearch)}
                className="h-5 w-5 text-red-600 focus:ring-red-500 border-gray-300 rounded"
              />
              <label htmlFor="groupSearch" className="ml-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
                Buscar por grupo
              </label>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {allowGroupSearch && isGroupSearch ? (
              <>
                <div className="md:col-span-2">
                  <label className={labelClass}>
                    SELECCIONAR GRUPO
                  </label>
                  <select
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                    className={selectClass}
                  >
                    <option value="">SELECCIONE EL GRUPO</option>
                    {groups.map((group) => (
                      <option key={group.idGrupo} value={group.nombre}>
                        {group.nombre}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:self-end">
                  <button
                    onClick={handleSearch}
                    disabled={!selectedGroup || loading}
                    className={primaryButtonClass}
                  >
                    {loading ? 'BUSCANDO...' : 'BUSCAR'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="md:col-span-2">
                  <label className={labelClass}>
                    BUSCAR CLIENTE
                  </label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
                    placeholder="INGRESE DNI, ID CLIENTE, NOMBRE O APELLIDOS"
                    className={inputClass}
                  />
                </div>
                <div className="md:self-end">
                  <button
                    onClick={handleSearch}
                    disabled={!searchTerm || loading}
                    className={primaryButtonClass}
                  >
                    {loading ? 'BUSCANDO...' : 'BUSCAR'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
          {error}
        </div>
      )}

      {prestamos.length > 0 && (
        <section className="ui-card p-6">
          <h3 className="mb-4 text-xl font-semibold text-slate-900">PRÉSTAMOS ENCONTRADOS</h3>
          <div className="max-w-full overflow-x-auto rounded-xl border border-slate-200">
            <table className="min-w-[1060px] text-sm text-center">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-center text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                  <th className="py-4 px-6">ID PRÉSTAMO</th>
                  <th className="py-4 px-6 min-w-[130px] whitespace-nowrap">MONTO</th>
                  <th className="py-4 px-6">FRECUENCIA</th>
                  <th className="py-4 px-6">CUOTAS</th>
                  <th className="py-4 px-6">FECHA INICIO</th>
                  <th className="py-4 px-6">HORA COBRANZA</th>
                  <th className="py-4 px-6 text-left">CLIENTE</th>
                  <th className="py-4 px-6">DNI</th>
                  <th className="py-4 px-6">ACCIÓN</th>
                </tr>
              </thead>
              <tbody>
                {prestamos.map((prestamo) => (
                  <tr
                    key={prestamo.idPrestamo}
                    className="border-b border-slate-200 transition hover:bg-slate-50"
                  >
                    <td className="py-4 px-6">{prestamo.idPrestamo}</td>
                    <td className="py-4 px-6 min-w-[130px] whitespace-nowrap tabular-nums">S/ {parseFloat(prestamo.monto).toFixed(2)}</td>
                    <td className="py-4 px-6">{prestamo.frecuencia}</td>
                    <td className="py-4 px-6">{prestamo.cuotas}</td>
                    <td className="py-4 px-6">{formatDate(prestamo.fecha_inicio)}</td>
                    <td className="py-4 px-6">{prestamo.hora_cobranza || '--:--'}</td>
                    <td className="py-4 px-6 text-left">{prestamo.cliente}</td>
                    <td className="py-4 px-6">{prestamo.dni}</td>
                    <td className="py-4 px-6">
                      <button
                        onClick={() => {
                          setSelectedPrestamo(prestamo.idPrestamo);
                          handleGenerateCronograma(prestamo.idPrestamo);
                        }}
                        className="rounded-lg bg-red-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-red-700"
                      >
                        VER CRONOGRAMA
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {cuotas.length > 0 && (
        <section className="ui-card p-6">
          <h3 className="mb-4 text-xl font-semibold text-slate-900">
            CRONOGRAMA DE PAGOS {esReprogramado && '(REPROGRAMADO)'}
          </h3>

          <div className="mb-6 flex flex-wrap gap-3">
            <button
              onClick={() => setShowReporte(true)}
              className={`${actionButtonClass} bg-yellow-500 hover:bg-yellow-600`}
            >
              DESCARGAR PDF
            </button>
            <button
              onClick={() => setShowCapturaAbonoModal(true)}
              className={`${actionButtonClass} bg-blue-600 hover:bg-blue-700`}
            >
              VER CAPTURA DE ABONO
            </button>
            <button
              type="button"
              aria-disabled="true"
              tabIndex={-1}
              className={`${actionButtonClass} cursor-default gap-2 bg-slate-700 text-white`}
              title={horaCobranza ? `Hora de cobranza: ${horaCobranza}` : 'Hora de cobranza sin registrar'}
            >
              <Clock className="h-4 w-4" aria-hidden="true" />
              HORA COBRANZA: {horaCobranza || '--:--'}
            </button>
            <div className="flex flex-col justify-center gap-2 self-center">
              <label className="inline-flex h-full min-h-[42px] items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                <input
                  type="checkbox"
                  checked={showHoraCobranzaEditor}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setShowHoraCobranzaEditor(checked);
                    if (!checked) {
                      setHoraCobranzaDraft(horaCobranza || '');
                    }
                  }}
                  className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                  disabled={savingHoraCobranza}
                />
                Modificar hora
              </label>
              {showHoraCobranzaEditor && (
                <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <input
                  type="time"
                  value={horaCobranzaDraft}
                  onChange={(event) => setHoraCobranzaDraft(event.target.value)}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200"
                  aria-label="Hora de cobranza"
                />
                <button
                  type="button"
                  onClick={handleSaveHoraCobranza}
                  disabled={savingHoraCobranza}
                  className="rounded-lg bg-red-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
                >
                  {savingHoraCobranza ? 'GUARDANDO...' : 'GUARDAR'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setHoraCobranzaDraft(horaCobranza || '');
                    setShowHoraCobranzaEditor(false);
                  }}
                  disabled={savingHoraCobranza}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  CANCELAR
                </button>
              </div>
              )}
            </div>
            {allowExtornar && isPrestamoCreadoHoy && (
              <button
                onClick={handleExtornarPrestamo}
                className={`${actionButtonClass} bg-red-600 hover:bg-red-700`}
              >
                EXTORNAR PRÉSTAMO
              </button>
            )}
          </div>

          <div className="max-w-full overflow-x-auto rounded-xl border border-slate-200">
            <table className="min-w-[1140px] text-sm text-center">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-center text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                  <th className="py-4 px-6">N°</th>
                  <th className="py-4 px-6">VENCIMIENTO</th>
                  <th className="py-4 px-6">CAPITAL</th>
                  <th className="py-4 px-6">INTERÉS</th>
                  <th className="py-4 px-6">OTROS</th>
                  <th className="py-4 px-6">CUOTA</th>
                  {mostrarColumnaEstado && <th className="py-4 px-6">ESTADO</th>}
                  <th className="py-4 px-6 w-1/4 text-left">OBSERVACIONES</th>
                </tr>
              </thead>
              <tbody>
                {cuotasAMostrar.map((cuota) => {
                  const otros =
                    cuota.otros !== null && cuota.otros !== undefined
                      ? parseFloat(cuota.otros)
                      : parseFloat(cuota.monto) - parseFloat(cuota.capital) - parseFloat(cuota.interes);

                  return (
                    <tr
                      key={cuota.idCuota}
                      className="border-b border-slate-200 transition hover:bg-slate-50"
                    >
                      <td className="py-4 px-6">{cuota.numero_cuota}</td>
                      <td className="py-4 px-6">{formatDate(cuota.fecha_vencimiento)}</td>
                      <td className="py-4 px-6">{parseFloat(cuota.capital).toFixed(2)}</td>
                      <td className="py-4 px-6">{parseFloat(cuota.interes).toFixed(2)}</td>
                      <td className="py-4 px-6">{otros.toFixed(2)}</td>
                      <td className="py-4 px-6">{parseFloat(cuota.monto).toFixed(2)}</td>

                      {mostrarColumnaEstado && (
                        <td className="py-4 px-6">
                          <span
                            className={`inline-block py-1 px-3 rounded-full text-xs font-medium ${cuota.estado === 'pagado' || cuota.estado === 'Pagado'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                              }`}
                          >
                            {cuota.estado === 'pagado' || cuota.estado === 'Pagado' ? 'PAGADO' : 'PENDIENTE'}
                          </span>
                        </td>
                      )}

                      <td className="py-4 px-6 text-left">{cuota.observaciones || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50 font-semibold text-slate-700">
                  <td colSpan="2" className="py-4 px-6">
                    TOTALES
                  </td>
                  <td className="py-4 px-6">
                    {totalesCalculados.capital.toFixed(2)}
                  </td>
                  <td className="py-4 px-6">
                    {totalesCalculados.interes.toFixed(2)}
                  </td>
                  <td className="py-4 px-6">
                    {totalesCalculados.otros.toFixed(2)}
                  </td>
                  <td className="py-4 px-6">
                    {totalesCalculados.cuota.toFixed(2)}
                  </td>
                  <td colSpan={mostrarColumnaEstado ? '2' : '1'} className="py-4 px-6"></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {showReporte && (
            <ReporteCronograma
              cuotas={cuotasAMostrar}
              prestamo={prestamoSeleccionado}
              cliente={clienteReporte}
              fechaEmision={new Date()}
              totales={totalesCalculados}
              onPDFGenerated={(url, nombreArchivo, blob) => {
                setPdfUrl(url);
                setPdfFileName(nombreArchivo);
                setPdfBlob(blob);
                setShowPreviewModal(true);
                setShowReporte(false);
              }}
              onComplete={() => setShowReporte(false)}
            />
          )}

          {showPreviewModal && pdfUrl && (
            <div className="modal-overlay">
              <div className="modal-panel modal-panel-lg flex h-[85vh] flex-col">
                <div className="flex items-center justify-between border-b border-slate-200 p-4">
                  <h2 className="text-lg font-semibold text-slate-900">Vista previa del cronograma</h2>
                  <div className="flex gap-2">
                    <button
                      onClick={handleDownloadPDF}
                      className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-700"
                    >
                      Descargar
                    </button>
                    <button
                      onClick={() => {
                        setShowPreviewModal(false);
                        URL.revokeObjectURL(pdfUrl);
                      }}
                      className="text-slate-500 transition hover:text-slate-700"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="flex-1 p-4">
                  <iframe
                    src={pdfUrl}
                    className="h-full w-full border-0"
                    title="PDF Preview"
                  />
                </div>
              </div>
            </div>
          )}

          {allowExtornar && showExtornarModal && (
            <ExtornarPrestamoModal
              show={showExtornarModal}
              onClose={() => setShowExtornarModal(false)}
              selectedPrestamo={prestamoSeleccionado}
              handleConfirmExtornar={registrarExtornoPrestamo}
            />
          )}

          {showCapturaAbonoModal && (
            <CapturaAbonoModal
              show={showCapturaAbonoModal}
              onClose={() => setShowCapturaAbonoModal(false)}
              clienteId={prestamoSeleccionado?.idCliente}
              prestamoId={prestamoSeleccionado?.idPrestamo}
              apiBasePath={apiBasePath}
              modalTitle="Captura de Abono del Préstamo"
            />
          )}
        </section>
      )}
    </div>
  );
};

export default Cronograma;
